import React, { useState, useEffect, useCallback } from 'react';
import { leaveTypeAPI, leaveBalanceAPI, employeeAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { DatePicker } from '../../components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Settings,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Gift,
  Users,
  Calendar,
  AlertCircle,
} from 'lucide-react';

const LeaveSettings = () => {
  const [activeTab, setActiveTab] = useState('types');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [allBalances, setAllBalances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isCompOffModalOpen, setIsCompOffModalOpen] = useState(false);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const [selectedType, setSelectedType] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedBalance, setSelectedBalance] = useState(null);
  
  const [typeData, setTypeData] = useState({
    name: '',
    annual_entitlement: 0,
    carry_forward_limit: 0,
    is_active: true,
    company: 'both',
  });
  
  const [compOffData, setCompOffData] = useState({
    employee_id: '',
    days: 1,
    date_worked: '',
    reason: '',
  });
  
  const [adjustData, setAdjustData] = useState({
    employee_id: '',
    leave_type_id: '',
    adjustment: 0,
    reason: '',
  });
  
  const [saving, setSaving] = useState(false);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const response = await leaveTypeAPI.getAll();
      setLeaveTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
    }
  }, []);

  const fetchAllBalances = useCallback(async () => {
    try {
      const response = await leaveBalanceAPI.getAll();
      setAllBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch balances:', error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.data.filter(e => e.is_active));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaveTypes();
    fetchEmployees();
  }, [fetchLeaveTypes, fetchEmployees]);

  useEffect(() => {
    if (activeTab === 'balances') {
      fetchAllBalances();
    }
  }, [activeTab, fetchAllBalances]);

  const handleSeedDefaults = async () => {
    setSaving(true);
    try {
      const response = await leaveTypeAPI.seedDefaults();
      alert(response.data.message);
      await fetchLeaveTypes();
    } catch (error) {
      console.error('Failed to seed defaults:', error);
      alert(error.response?.data?.detail || 'Failed to seed defaults');
    } finally {
      setSaving(false);
    }
  };

  const openTypeModal = (type = null) => {
    if (type) {
      setSelectedType(type);
      setTypeData({
        name: type.name,
        annual_entitlement: type.annual_entitlement,
        carry_forward_limit: type.carry_forward_limit,
        is_active: type.is_active,
        company: type.company,
      });
    } else {
      setSelectedType(null);
      setTypeData({
        name: '',
        annual_entitlement: 0,
        carry_forward_limit: 0,
        is_active: true,
        company: 'both',
      });
    }
    setIsTypeModalOpen(true);
  };

  const handleSaveType = async () => {
    setSaving(true);
    try {
      if (selectedType) {
        await leaveTypeAPI.update(selectedType.id, typeData);
      } else {
        await leaveTypeAPI.create(typeData);
      }
      await fetchLeaveTypes();
      setIsTypeModalOpen(false);
    } catch (error) {
      console.error('Failed to save leave type:', error);
      alert(error.response?.data?.detail || 'Failed to save leave type');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteType = async () => {
    try {
      await leaveTypeAPI.delete(selectedType.id);
      await fetchLeaveTypes();
      setIsDeleteDialogOpen(false);
      setSelectedType(null);
    } catch (error) {
      console.error('Failed to delete leave type:', error);
    }
  };

  const handleCreditCompOff = async () => {
    setSaving(true);
    try {
      const response = await leaveBalanceAPI.creditCompOff(compOffData);
      alert(response.data.message);
      setIsCompOffModalOpen(false);
      setCompOffData({ employee_id: '', days: 1, date_worked: '', reason: '' });
      if (activeTab === 'balances') {
        await fetchAllBalances();
      }
    } catch (error) {
      console.error('Failed to credit comp-off:', error);
      alert(error.response?.data?.detail || 'Failed to credit comp-off');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustBalance = async () => {
    setSaving(true);
    try {
      const response = await leaveBalanceAPI.adjust(adjustData);
      alert(response.data.message);
      setIsAdjustModalOpen(false);
      setAdjustData({ employee_id: '', leave_type_id: '', adjustment: 0, reason: '' });
      await fetchAllBalances();
    } catch (error) {
      console.error('Failed to adjust balance:', error);
      alert(error.response?.data?.detail || 'Failed to adjust balance');
    } finally {
      setSaving(false);
    }
  };

  const handleAnnualReset = async () => {
    setSaving(true);
    try {
      const response = await leaveBalanceAPI.annualReset(true);
      alert(response.data.message);
      setIsResetDialogOpen(false);
      await fetchAllBalances();
    } catch (error) {
      console.error('Failed to reset balances:', error);
      alert(error.response?.data?.detail || 'Failed to reset balances');
    } finally {
      setSaving(false);
    }
  };

  const openAdjustModal = (empBalance, balance) => {
    setSelectedEmployee(empBalance);
    setSelectedBalance(balance);
    setAdjustData({
      employee_id: empBalance.employee_id,
      leave_type_id: balance.leave_type_id,
      adjustment: 0,
      reason: '',
    });
    setIsAdjustModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="leave-settings-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">Leave Settings</h2>
          <p className="text-slate-500 text-sm mt-1">
            Configure leave types, manage balances, and credit comp-offs
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-flex">
          <TabsTrigger value="types" data-testid="tab-types">
            <Settings className="w-4 h-4 mr-2" />
            Leave Types
          </TabsTrigger>
          <TabsTrigger value="balances" data-testid="tab-balances">
            <Users className="w-4 h-4 mr-2" />
            Employee Balances
          </TabsTrigger>
        </TabsList>

        {/* Leave Types Tab */}
        <TabsContent value="types" className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleSeedDefaults}
              variant="outline"
              disabled={saving}
              data-testid="seed-defaults-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Default Types
            </Button>
            <Button
              onClick={() => openTypeModal()}
              className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
              data-testid="add-type-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Leave Type
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full" data-testid="leave-types-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Entitlement</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Carry Forward</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {leaveTypes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      No leave types configured. Click "Seed Default Types" to get started.
                    </td>
                  </tr>
                ) : (
                  leaveTypes.map((type) => (
                    <tr key={type.id} className="hover:bg-slate-50" data-testid={`type-row-${type.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{type.name}</td>
                      <td className="px-4 py-3 text-center text-slate-600">
                        {type.annual_entitlement === 999 ? 'Unlimited' : `${type.annual_entitlement} days`}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">
                        {type.carry_forward_limit > 0 ? `${type.carry_forward_limit} days` : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          type.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {type.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openTypeModal(type)}
                          data-testid={`edit-type-${type.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => {
                            setSelectedType(type);
                            setIsDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-type-${type.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Employee Balances Tab */}
        <TabsContent value="balances" className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setIsCompOffModalOpen(true)}
              className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
              data-testid="credit-compoff-btn"
            >
              <Gift className="w-4 h-4 mr-2" />
              Credit Comp-off
            </Button>
            <Button
              onClick={() => setIsResetDialogOpen(true)}
              variant="outline"
              className="text-amber-600 border-amber-300 hover:bg-amber-50"
              data-testid="annual-reset-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Annual Reset
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="balances-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky left-0 bg-slate-50 z-10 min-w-[180px]">
                      Employee
                    </th>
                    {leaveTypes.filter(t => t.is_active).map((type) => (
                      <th key={type.id} className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase min-w-[100px]">
                        {type.name.replace('/', '/ ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {allBalances.map((empBal) => (
                    <tr key={empBal.employee_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 sticky left-0 bg-white z-10 border-r border-slate-200">
                        <p className="font-medium text-slate-900">{empBal.employee_name}</p>
                      </td>
                      {leaveTypes.filter(t => t.is_active).map((type) => {
                        const balance = empBal.balances.find(b => b.leave_type_id === type.id);
                        return (
                          <td
                            key={type.id}
                            className="px-3 py-3 text-center cursor-pointer hover:bg-blue-50"
                            onClick={() => balance && openAdjustModal(empBal, balance)}
                            title="Click to adjust"
                          >
                            {balance ? (
                              <div>
                                <span className={`font-medium ${balance.balance <= 0 ? 'text-red-600' : 'text-slate-900'}`}>
                                  {balance.balance}
                                </span>
                                <span className="text-slate-400 text-xs"> / {balance.entitlement}</span>
                              </div>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            Click on any balance to manually adjust it.
          </p>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Leave Type Modal */}
      <Dialog open={isTypeModalOpen} onOpenChange={setIsTypeModalOpen}>
        <DialogContent className="max-w-md" data-testid="type-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              {selectedType ? 'Edit Leave Type' : 'Add Leave Type'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Configure leave type settings
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={typeData.name}
                onChange={(e) => setTypeData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Casual Leave"
                data-testid="input-type-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Annual Entitlement (days)</Label>
                <Input
                  type="number"
                  value={typeData.annual_entitlement}
                  onChange={(e) => setTypeData(prev => ({ ...prev, annual_entitlement: parseInt(e.target.value) || 0 }))}
                  data-testid="input-entitlement"
                />
              </div>
              <div className="space-y-2">
                <Label>Carry Forward Limit</Label>
                <Input
                  type="number"
                  value={typeData.carry_forward_limit}
                  onChange={(e) => setTypeData(prev => ({ ...prev, carry_forward_limit: parseInt(e.target.value) || 0 }))}
                  data-testid="input-carry-forward"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Company</Label>
              <Select
                value={typeData.company}
                onValueChange={(value) => setTypeData(prev => ({ ...prev, company: value }))}
              >
                <SelectTrigger data-testid="select-company">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both Companies</SelectItem>
                  <SelectItem value="empact_studio">Empact Studio Only</SelectItem>
                  <SelectItem value="event_resource">Event Resource Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_active"
                checked={typeData.is_active}
                onCheckedChange={(checked) => setTypeData(prev => ({ ...prev, is_active: checked }))}
                data-testid="checkbox-active"
              />
              <Label htmlFor="is_active" className="text-sm font-normal cursor-pointer">
                Active
              </Label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsTypeModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveType}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={saving || !typeData.name}
                data-testid="save-type-btn"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credit Comp-off Modal */}
      <Dialog open={isCompOffModalOpen} onOpenChange={setIsCompOffModalOpen}>
        <DialogContent className="max-w-md" data-testid="compoff-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              Credit Comp-off Days
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Credit compensatory off days to an employee
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select
                value={compOffData.employee_id}
                onValueChange={(value) => setCompOffData(prev => ({ ...prev, employee_id: value }))}
              >
                <SelectTrigger data-testid="select-compoff-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Days to Credit</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={compOffData.days}
                  onChange={(e) => setCompOffData(prev => ({ ...prev, days: parseFloat(e.target.value) || 1 }))}
                  data-testid="input-compoff-days"
                />
              </div>
              <div className="space-y-2">
                <Label>Date Worked</Label>
                <DatePicker
                  value={compOffData.date_worked}
                  onChange={(v) => setCompOffData(prev => ({ ...prev, date_worked: v }))}
                  placeholder="Pick date"
                  data-testid="input-compoff-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                value={compOffData.reason}
                onChange={(e) => setCompOffData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Worked on Republic Day"
                rows={2}
                data-testid="input-compoff-reason"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsCompOffModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreditCompOff}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={saving || !compOffData.employee_id || !compOffData.reason}
                data-testid="save-compoff-btn"
              >
                {saving ? 'Crediting...' : 'Credit Comp-off'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust Balance Modal */}
      <Dialog open={isAdjustModalOpen} onOpenChange={setIsAdjustModalOpen}>
        <DialogContent className="max-w-md" data-testid="adjust-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              Adjust Leave Balance
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedEmployee?.employee_name} - {selectedBalance?.leave_type_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">
                Current Balance: <span className="font-semibold">{selectedBalance?.balance}</span> / {selectedBalance?.entitlement} days
              </p>
            </div>

            <div className="space-y-2">
              <Label>Adjustment (days)</Label>
              <Input
                type="number"
                step="0.5"
                value={adjustData.adjustment}
                onChange={(e) => setAdjustData(prev => ({ ...prev, adjustment: parseFloat(e.target.value) || 0 }))}
                placeholder="Positive to add, negative to deduct"
                data-testid="input-adjustment"
              />
              <p className="text-xs text-slate-500">
                Use positive number to add, negative to deduct
              </p>
            </div>

            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                value={adjustData.reason}
                onChange={(e) => setAdjustData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Reason for adjustment"
                rows={2}
                data-testid="input-adjust-reason"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsAdjustModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAdjustBalance}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={saving || !adjustData.reason || adjustData.adjustment === 0}
                data-testid="save-adjust-btn"
              >
                {saving ? 'Adjusting...' : 'Adjust Balance'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Leave Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedType?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteType}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Annual Reset Confirmation */}
      <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annual Balance Reset</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset all leave balances to their annual entitlement for the new year.
              Unused Earned/Privilege Leave will be carried forward up to the configured limit.
              <br /><br />
              <strong>This action affects all employees and cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAnnualReset}
              className="bg-amber-600 hover:bg-amber-700"
              disabled={saving}
            >
              {saving ? 'Resetting...' : 'Reset All Balances'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeaveSettings;
